import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Calendar, Clock, Users, DollarSign, BarChart3, Shield, AlertTriangle,
  CheckCircle, XCircle, ArrowRight, TrendingUp, TrendingDown, Building2,
  MapPin, Phone, FileText, Download, Upload, Search, Filter, RefreshCw,
  ChevronDown, ChevronRight, Activity, Briefcase, UserCheck, UserX, Star,
  Zap, Globe, Award, ClipboardList, Timer, Bell, Settings, PieChart, Target,
  Layers, GitBranch, Truck, CreditCard, FileSpreadsheet, Stethoscope,
  HeartPulse, BedDouble, Thermometer, Printer, ChevronLeft
} from 'lucide-react';
import EnterpriseRosterService from '../../services/rostering/EnterpriseRosterService';
import { useToast } from '../Common/ToastProvider';
import { EmptyState, Tooltip } from '../Common/ReusableUI';
import { TableSkeleton, CardSkeleton } from '../Common/SkeletonLoaders';

const TAB_DEFS = [
  { id: 'overview', label: 'Workforce Overview', icon: Users },
  { id: 'demand', label: 'Demand Engine', icon: Activity },
  { id: 'leave', label: 'Leave Management', icon: Calendar },
  { id: 'timesheets', label: 'Timesheets', icon: Clock },
  { id: 'marketplace', label: 'Marketplace', icon: Globe },
  { id: 'payroll', label: 'Payroll', icon: CreditCard },
  { id: 'compliance', label: 'Compliance', icon: Shield },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'oncall', label: 'On-Call', icon: Phone },
];

const fmt$ = (v) => `$${(v || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (v) => `${(v || 0).toFixed(1)}%`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : '—';
const toDateStr = (d) => (d instanceof Date ? d : new Date(d)).toISOString().split('T')[0];

const StatusBadge = ({ status, className = '' }) => {
  const colors = {
    approved: 'bg-emerald-100 text-emerald-800',
    pending: 'bg-amber-100 text-amber-800',
    declined: 'bg-red-100 text-red-800',
    active: 'bg-emerald-100 text-emerald-800',
    scheduled: 'bg-blue-100 text-blue-800',
    completed: 'bg-gray-100 text-gray-700',
    open: 'bg-blue-100 text-blue-800',
    filled: 'bg-emerald-100 text-emerald-800',
    expired: 'bg-red-100 text-red-800',
    generated: 'bg-blue-100 text-blue-800',
    exported: 'bg-emerald-100 text-emerald-800',
    auto_generated: 'bg-purple-100 text-purple-800',
    reviewed: 'bg-indigo-100 text-indigo-800',
    ready_for_review: 'bg-amber-100 text-amber-800',
  };
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-bold uppercase ${colors[status] || 'bg-gray-100 text-gray-700'} ${className}`}>
      {(status || 'unknown').replace(/_/g, ' ')}
    </span>
  );
};

const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${className}`}>{children}</div>
);

const MetricCard = ({ label, value, sub, icon: Icon, color = 'indigo', trend, trendDir }) => (
  <Card className="p-5">
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
        {trend !== undefined && (
          <div className={`flex items-center gap-1 mt-1.5 text-xs font-medium ${trendDir === 'up' ? 'text-emerald-600' : trendDir === 'down' ? 'text-red-600' : 'text-gray-500'}`}>
            {trendDir === 'up' ? <TrendingUp className="w-3 h-3" /> : trendDir === 'down' ? <TrendingDown className="w-3 h-3" /> : null}
            {trend}
          </div>
        )}
      </div>
      <div className={`w-11 h-11 bg-${color}-100 rounded-xl flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-5 h-5 text-${color}-600`} />
      </div>
    </div>
  </Card>
);

const acuityColor = (level) => {
  const map = { critical: 'bg-red-500 text-white', high: 'bg-orange-400 text-white', medium: 'bg-amber-300 text-gray-900', low: 'bg-emerald-200 text-gray-900' };
  return map[level] || 'bg-gray-200 text-gray-700';
};

const demandIndicator = (required, rostered) => {
  if (rostered >= required) return 'text-emerald-600';
  if (rostered >= required * 0.8) return 'text-amber-600';
  return 'text-red-600';
};

const EnterpriseRoster = () => {
  const { showToast } = useToast();
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Data states ──
  const [multiFacility, setMultiFacility] = useState(null);
  const [floatPool, setFloatPool] = useState(null);
  const [demandData, setDemandData] = useState(null);
  const [demandAlerts, setDemandAlerts] = useState([]);
  const [heatmapData, setHeatmapData] = useState(null);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [leaveLiability, setLeaveLiability] = useState(null);
  const [blackoutPeriods, setBlackoutPeriods] = useState([]);
  const [leaveCalendar, setLeaveCalendar] = useState(null);
  const [timesheets, setTimesheets] = useState([]);
  const [payPeriodSummary, setPayPeriodSummary] = useState(null);
  const [marketplaceShifts, setMarketplaceShifts] = useState([]);
  const [marketplaceAnalytics, setMarketplaceAnalytics] = useState(null);
  const [payRunPreview, setPayRunPreview] = useState(null);
  const [payRunHistory, setPayRunHistory] = useState([]);
  const [careMinutesReport, setCareMinutesReport] = useState(null);
  const [rnDeclaration, setRnDeclaration] = useState(null);
  const [nqipIndicators, setNqipIndicators] = useState(null);
  const [starRating, setStarRating] = useState(null);
  const [analyticsMetrics, setAnalyticsMetrics] = useState(null);
  const [turnoverPrediction, setTurnoverPrediction] = useState(null);
  const [onCallRoster, setOnCallRoster] = useState([]);
  const [escalationHistory, setEscalationHistory] = useState(null);

  // ── Filter / UI states ──
  const [searchQuery, setSearchQuery] = useState('');
  const [facilityFilter, setFacilityFilter] = useState('sunshine');
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showTimesheetApproveModal, setShowTimesheetApproveModal] = useState(null);
  const [showBlackoutModal, setShowBlackoutModal] = useState(false);
  const [selectedMarketplaceShift, setSelectedMarketplaceShift] = useState(null);
  const [payPeriodStart, setPayPeriodStart] = useState('');
  const [payPeriodEnd, setPayPeriodEnd] = useState('');
  const [scenarioOccupancy, setScenarioOccupancy] = useState(82);
  const [scenarioAgency, setScenarioAgency] = useState(8);
  const [scenarioOvertime, setScenarioOvertime] = useState(5);
  const [scenarioCost, setScenarioCost] = useState(null);
  const [alertSidebarOpen, setAlertSidebarOpen] = useState(false);
  const [reassignDropdown, setReassignDropdown] = useState(null);

  // ── Set pay period defaults ──
  useEffect(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setPayPeriodStart(toDateStr(start));
    setPayPeriodEnd(toDateStr(end));
  }, []);

  // ── Data loading ──
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const now = new Date();
      const today = toDateStr(now);
      const weekFromNow = toDateStr(new Date(now.getTime() + 7 * 86400000));
      const monthStart = toDateStr(new Date(now.getFullYear(), now.getMonth(), 1));
      const monthEnd = toDateStr(new Date(now.getFullYear(), now.getMonth() + 1, 0));

      const [
        mfResult, fpResult, demandRes, alertsRes, heatRes,
        leaveRes, liabilityRes, blackoutRes, calendarRes,
        tsResult, ppSummary, mktResult, mktAnalytics,
        payPreview, payHistory, cmReport, rnDecl, nqipRes,
        starRes, metricsRes, turnoverRes, oncallRes, escRes
      ] = await Promise.all([
        EnterpriseRosterService.getMultiFacilityDashboard('medfast'),
        EnterpriseRosterService.getFloatPoolStaff('medfast'),
        EnterpriseRosterService.getDemandDashboard(facilityFilter, today, weekFromNow),
        EnterpriseRosterService.getDemandAlerts(facilityFilter),
        EnterpriseRosterService.getAcuityHeatmap(facilityFilter),
        EnterpriseRosterService.getLeaveRequests({}),
        EnterpriseRosterService.getLeaveLiabilityReport(facilityFilter),
        EnterpriseRosterService.getBlackoutPeriods(facilityFilter),
        EnterpriseRosterService.getLeaveCalendar(facilityFilter, monthStart, monthEnd),
        EnterpriseRosterService.getTimesheets({}),
        EnterpriseRosterService.getPayPeriodSummary(facilityFilter, monthStart, monthEnd),
        EnterpriseRosterService.getMarketplaceShifts({}),
        EnterpriseRosterService.getMarketplaceAnalytics(facilityFilter),
        EnterpriseRosterService.previewPayRun(facilityFilter, monthStart, monthEnd),
        EnterpriseRosterService.getPayRunHistory(facilityFilter),
        EnterpriseRosterService.generateCareMinutesReport(facilityFilter, now.getMonth() + 1, now.getFullYear()),
        EnterpriseRosterService.generateRNCoverageDeclaration(facilityFilter, now.getMonth() + 1, now.getFullYear()),
        EnterpriseRosterService.generateNQIPWorkforceIndicators(facilityFilter, Math.ceil((now.getMonth() + 1) / 3), now.getFullYear()),
        EnterpriseRosterService.calculateStarRatingStaffing(facilityFilter),
        EnterpriseRosterService.getOperationalMetrics(facilityFilter),
        EnterpriseRosterService.getTurnoverPrediction(facilityFilter),
        EnterpriseRosterService.getOnCallRoster(facilityFilter, { startDate: today, endDate: weekFromNow }),
        EnterpriseRosterService.getEscalationHistory(facilityFilter),
      ]);

      if (mfResult.success) setMultiFacility(mfResult.data);
      if (fpResult.success) setFloatPool(fpResult.data);
      if (demandRes.success) setDemandData(demandRes.data);
      if (alertsRes.success) setDemandAlerts(alertsRes.data.alerts || []);
      if (heatRes.success) setHeatmapData(heatRes.data);
      if (leaveRes.success) setLeaveRequests(leaveRes.data);
      if (liabilityRes.success) setLeaveLiability(liabilityRes.data);
      if (blackoutRes.success) setBlackoutPeriods(blackoutRes.data);
      if (calendarRes.success) setLeaveCalendar(calendarRes.data);
      if (tsResult.success) setTimesheets(tsResult.data);
      if (ppSummary.success) setPayPeriodSummary(ppSummary.data);
      if (mktResult.success) setMarketplaceShifts(mktResult.data);
      if (mktAnalytics.success) setMarketplaceAnalytics(mktAnalytics.data);
      if (payPreview.success) setPayRunPreview(payPreview.data);
      if (payHistory.success) setPayRunHistory(payHistory.data);
      if (cmReport.success) setCareMinutesReport(cmReport.data);
      if (rnDecl.success) setRnDeclaration(rnDecl.data);
      if (nqipRes.success) setNqipIndicators(nqipRes.data);
      if (starRes.success) setStarRating(starRes.data);
      if (metricsRes.success) setAnalyticsMetrics(metricsRes.data);
      if (turnoverRes.success) setTurnoverPrediction(turnoverRes.data);
      if (oncallRes.success) setOnCallRoster(oncallRes.data);
      if (escRes.success) setEscalationHistory(escRes.data);

      // Load leave balances for known staff
      const staffIds = ['staff-james-chen', 'staff-emma-thompson', 'staff-david-rodriguez', 'staff-michael-brown', 'staff-sophie-wilson', 'staff-grace-lee', 'staff-daniel-wang', 'staff-hannah-smith'];
      const balResults = await Promise.all(staffIds.map(id => EnterpriseRosterService.getLeaveBalances(id)));
      setLeaveBalances(balResults.filter(r => r.success).map(r => r.data));

      console.log('[EnterpriseRoster] All data loaded successfully');
    } catch (err) {
      console.error('[EnterpriseRoster] Load error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [facilityFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Action handlers ──
  const handleApproveLeave = useCallback(async (requestId) => {
    const res = await EnterpriseRosterService.approveLeaveRequest(requestId, 'Admin');
    if (res.success) {
      setLeaveRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: 'approved' } : r));
    }
  }, []);

  const handleDeclineLeave = useCallback(async (requestId) => {
    const res = await EnterpriseRosterService.declineLeaveRequest(requestId, 'Scheduling conflict', 'Admin');
    if (res.success) {
      setLeaveRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: 'declined' } : r));
    }
  }, []);

  const handleApproveTimesheet = useCallback(async (tsId) => {
    const res = await EnterpriseRosterService.approveTimesheet(tsId, 'Admin');
    if (res.success) {
      setTimesheets(prev => prev.map(t => t.id === tsId ? { ...t, status: 'approved' } : t));
      setShowTimesheetApproveModal(null);
    }
  }, []);

  const handleBulkApproveTimesheets = useCallback(async () => {
    const eligible = timesheets.filter(t => t.status !== 'approved' && t.status !== 'exported');
    for (const ts of eligible) {
      await EnterpriseRosterService.approveTimesheet(ts.id, 'Admin');
    }
    setTimesheets(prev => prev.map(t => eligible.find(e => e.id === t.id) ? { ...t, status: 'approved' } : t));
  }, [timesheets]);

  const handleSelectApplicant = useCallback(async (shiftId, staffId) => {
    const res = await EnterpriseRosterService.selectApplicant(shiftId, staffId, 'Admin');
    if (res.success) {
      setMarketplaceShifts(prev => prev.map(s => s.id === shiftId ? { ...s, status: 'filled', selectedStaffId: staffId } : s));
      setSelectedMarketplaceShift(null);
    }
  }, []);

  const handleRunScenario = useCallback(async () => {
    const res = await EnterpriseRosterService.runScenario(facilityFilter, { occupancy: scenarioOccupancy });
    if (res.success) setScenarioCost(res.data);
  }, [facilityFilter, scenarioOccupancy]);

  const handleRespondEscalation = useCallback(async (escId) => {
    await EnterpriseRosterService.respondToEscalation(escId, 'Admin', 'resolved');
    loadData();
  }, [loadData]);

  // ── Computed values ──
  const pendingLeave = useMemo(() => leaveRequests.filter(r => r.status === 'pending'), [leaveRequests]);
  const approvedLeave = useMemo(() => leaveRequests.filter(r => r.status === 'approved'), [leaveRequests]);
  const openMarketplace = useMemo(() => marketplaceShifts.filter(s => s.status === 'open'), [marketplaceShifts]);
  const orgTotalStaff = multiFacility?.facilities?.reduce((s, f) => s + (f.operationalMetrics?.totalStaff || 0), 0) || 0;

  const filteredTimesheets = useMemo(() => {
    if (!searchQuery) return timesheets;
    const q = searchQuery.toLowerCase();
    return timesheets.filter(t => (t.staffName || t.staffId || '').toLowerCase().includes(q));
  }, [timesheets, searchQuery]);

  // ── Export helpers ──
  const handleExport = useCallback((data, filename) => {
    if (!data || data.length === 0) {
      showToast('No data to export', 'warning');
      return;
    }
    const headers = Object.keys(data[0]);
    const csvRows = [
      headers.join(','),
      ...data.map(row =>
        headers.map(h => {
          const val = row[h] ?? '';
          const str = String(val).replace(/"/g, '""');
          return `"${str}"`;
        }).join(',')
      ),
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast(`Exported ${data.length} rows to ${link.download}`, 'success');
    console.log(`[EnterpriseRoster] CSV exported: ${filename}, ${data.length} rows`);
  }, [showToast]);

  const handlePrint = useCallback((sectionTitle) => {
    document.title = `MedFast — ${sectionTitle}`;
    window.print();
    document.title = 'MedFast Aged Care';
    console.log(`[EnterpriseRoster] Print triggered for: ${sectionTitle}`);
  }, []);

  const handleQuickApproveTimesheet = useCallback(async (tsId) => {
    const res = await EnterpriseRosterService.approveTimesheet(tsId, 'Admin');
    if (res.success) {
      setTimesheets(prev => prev.map(t => t.id === tsId ? { ...t, status: 'approved' } : t));
      showToast('Timesheet approved', 'success');
    }
  }, [showToast]);

  const handleSendReminder = useCallback((staffName) => {
    showToast(`Reminder sent to ${staffName}`, 'success');
    console.log(`[EnterpriseRoster] Reminder sent to ${staffName}`);
  }, [showToast]);

  // ── Computed alerts for sidebar ──
  const rnCoverageGaps = useMemo(() => rnDeclaration?.gaps || [], [rnDeclaration]);

  const overdueLeaveRequests = useMemo(() => {
    const today = toDateStr(new Date());
    return leaveRequests.filter(r => r.status === 'pending' && r.startDate <= today);
  }, [leaveRequests]);

  const unfilledShifts = useMemo(() => marketplaceShifts.filter(s => s.status === 'open'), [marketplaceShifts]);

  const credentialExpirations = useMemo(() => {
    return demandAlerts.filter(a =>
      a.message?.toLowerCase().includes('credential') ||
      a.message?.toLowerCase().includes('expir')
    );
  }, [demandAlerts]);

  const totalAlertCount = useMemo(() =>
    rnCoverageGaps.length + overdueLeaveRequests.length + unfilledShifts.length + credentialExpirations.length,
    [rnCoverageGaps, overdueLeaveRequests, unfilledShifts, credentialExpirations]
  );

  // ── Loading state ──
  if (loading) {
    return (
      <div className="space-y-6 print:hidden" role="status" aria-label="Loading Enterprise Roster">
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 rounded-2xl p-8">
          <div className="flex gap-6 items-center">
            <div className="w-20 h-20 bg-white bg-opacity-20 rounded-2xl animate-pulse" />
            <div className="space-y-3 flex-1">
              <div className="h-8 bg-white bg-opacity-20 rounded-lg w-80 animate-pulse" />
              <div className="h-4 bg-white bg-opacity-10 rounded w-96 animate-pulse" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)}
        </div>
        <TableSkeleton rows={6} columns={6} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <CardSkeleton /><CardSkeleton />
        </div>
        <p className="text-center text-sm text-gray-400 animate-pulse">Aggregating multi-facility workforce data…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
        <h3 className="text-lg font-bold text-red-800">Failed to load roster data</h3>
        <p className="text-sm text-red-600 mt-1">{error}</p>
        <button onClick={loadData} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 print:space-y-2 relative">
      {/* ═══════════════════ Alert Sidebar ═══════════════════ */}
      {!alertSidebarOpen && totalAlertCount > 0 && (
        <button
          onClick={() => setAlertSidebarOpen(true)}
          className="fixed right-4 top-24 z-40 bg-red-600 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg hover:bg-red-700 transition-all print:hidden"
          aria-label={`${totalAlertCount} alerts — click to expand`}
        >
          <Bell className="w-5 h-5" />
          <span className="absolute -top-1 -right-1 bg-white text-red-600 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow">
            {totalAlertCount}
          </span>
        </button>
      )}

      {alertSidebarOpen && (
        <div className="fixed right-0 top-0 z-50 h-full w-80 bg-white shadow-2xl border-l border-gray-200 overflow-y-auto print:hidden">
          <div className="bg-gradient-to-r from-red-600 to-rose-600 text-white p-4 flex justify-between items-center sticky top-0 z-10">
            <div>
              <h3 className="font-bold text-lg">Critical Alerts</h3>
              <p className="text-red-200 text-xs">{totalAlertCount} items require attention</p>
            </div>
            <button onClick={() => setAlertSidebarOpen(false)} className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg" aria-label="Close alerts">
              <ChevronLeft className="w-5 h-5 rotate-180" />
            </button>
          </div>
          <div className="p-4 space-y-4">
            {/* RN Coverage Gaps */}
            {rnCoverageGaps.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-red-700 flex items-center gap-1.5 mb-2">
                  <HeartPulse className="w-4 h-4" /> RN Coverage Gaps ({rnCoverageGaps.length})
                </h4>
                {rnCoverageGaps.map((gap, i) => (
                  <div key={i} className="p-2 bg-red-50 rounded-lg mb-1.5 text-xs">
                    <span className="font-medium text-red-800">{fmtDate(gap.date)}</span>
                    <span className="text-red-600 ml-1">— {gap.shift} shift</span>
                    <p className="text-gray-500 mt-0.5">{gap.reason}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Overdue Leave Requests */}
            {overdueLeaveRequests.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-amber-700 flex items-center gap-1.5 mb-2">
                  <Calendar className="w-4 h-4" /> Overdue Leave Requests ({overdueLeaveRequests.length})
                </h4>
                {overdueLeaveRequests.map(req => (
                  <div key={req.id} className="p-2 bg-amber-50 rounded-lg mb-1.5 text-xs">
                    <span className="font-medium text-amber-800">{req.staffName}</span>
                    <p className="text-gray-500">{fmtDate(req.startDate)} — {fmtDate(req.endDate)}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Unfilled Marketplace Shifts */}
            {unfilledShifts.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-blue-700 flex items-center gap-1.5 mb-2">
                  <Globe className="w-4 h-4" /> Unfilled Shifts ({unfilledShifts.length})
                </h4>
                {unfilledShifts.slice(0, 5).map(shift => (
                  <div key={shift.id} className="p-2 bg-blue-50 rounded-lg mb-1.5 text-xs">
                    <span className="font-medium text-blue-800">{shift.facilityName || shift.facilityId}</span>
                    <span className="text-blue-600 ml-1">— {(shift.currentTier || '').replace(/_/g, ' ')}</span>
                    <p className="text-gray-500">{shift.applicants?.length || 0} applicants</p>
                  </div>
                ))}
                {unfilledShifts.length > 5 && (
                  <p className="text-xs text-gray-400 text-center">+{unfilledShifts.length - 5} more</p>
                )}
              </div>
            )}

            {/* Credential Expirations */}
            {credentialExpirations.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-orange-700 flex items-center gap-1.5 mb-2">
                  <Shield className="w-4 h-4" /> Credential Alerts ({credentialExpirations.length})
                </h4>
                {credentialExpirations.map((alert, i) => (
                  <div key={i} className="p-2 bg-orange-50 rounded-lg mb-1.5 text-xs">
                    <span className="font-medium text-orange-800">{alert.wardName || 'Facility'}</span>
                    <p className="text-gray-500">{alert.message}</p>
                  </div>
                ))}
              </div>
            )}

            {totalAlertCount === 0 && (
              <div className="text-center py-8">
                <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">All clear — no critical alerts</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════ Header Banner ═══════════════════ */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 rounded-2xl p-8 text-white shadow-2xl print:shadow-none print:rounded-none print:p-4">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
          <div className="flex items-center space-x-6">
            <div className="w-20 h-20 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center backdrop-blur-sm print:hidden">
              <Layers className="w-10 h-10 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold print:text-xl">Enterprise Workforce Management</h1>
              <p className="text-indigo-200 text-base mt-2 print:text-sm">
                Multi-facility rostering, demand engine, leave, timesheets, marketplace, payroll, compliance &amp; analytics
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 print:hidden">
            {[
              { label: 'Total Staff', value: orgTotalStaff, icon: Users },
              { label: 'Facilities', value: multiFacility?.facilityCount || 0, icon: Building2 },
              { label: 'Open Shifts', value: openMarketplace.length, icon: Globe },
              { label: 'Pending Leave', value: pendingLeave.length, icon: Calendar },
            ].map(s => (
              <div key={s.label} className="bg-white bg-opacity-15 backdrop-blur-sm rounded-xl px-5 py-3 text-center min-w-[100px]">
                <s.icon className="w-5 h-5 mx-auto mb-1 text-indigo-200" />
                <div className="text-2xl font-bold">{s.value}</div>
                <div className="text-xs text-indigo-200">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══════════════════ Tab Navigation ═══════════════════ */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-1.5 overflow-x-auto">
        <div className="flex space-x-1 min-w-max">
          {TAB_DEFS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeSubTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${
                  isActive
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-indigo-600 hover:bg-indigo-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Facility filter (shared) */}
      <div className="flex items-center gap-3">
        <Filter className="w-4 h-4 text-gray-400" />
        <select
          value={facilityFilter}
          onChange={e => setFacilityFilter(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500"
        >
          <option value="sunshine">Sunshine Gardens</option>
          <option value="bayview">Bayview Gardens</option>
        </select>
        <button onClick={loadData} className="flex items-center gap-1.5 px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          TAB 1 — Workforce Overview
          ═══════════════════════════════════════════════════════════════ */}
      {activeSubTab === 'overview' && (
        <div className="space-y-6">
          {/* Quick stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard label="Total Staff (Org)" value={orgTotalStaff} icon={Users} color="indigo" sub="Across all facilities" />
            <MetricCard label="On Duty Now" value={Math.round(orgTotalStaff * 0.35)} icon={UserCheck} color="emerald" sub="Current shift" />
            <MetricCard label="On Leave Today" value={approvedLeave.filter(r => r.startDate <= toDateStr(new Date()) && r.endDate >= toDateStr(new Date())).length} icon={Calendar} color="amber" sub="Approved leave" />
            <MetricCard label="Credential Alerts" value={demandAlerts.length} icon={AlertTriangle} color="red" sub="Requires attention" />
          </div>

          {/* Facility grid */}
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-indigo-600" /> Facility Dashboard
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {(multiFacility?.facilities || []).map(fac => {
              const metrics = fac.operationalMetrics || {};
              const careMin = metrics.careMinutesPerResidentPerDay || 0;
              const careColor = careMin >= 215 ? 'text-emerald-600' : careMin >= 200 ? 'text-amber-600' : 'text-red-600';
              return (
                <Card key={fac.facilityId} className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="text-lg font-bold text-gray-900">{fac.facilityName}</h4>
                      <p className="text-sm text-gray-500 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{fac.region}</p>
                    </div>
                    {fac.starRating && (
                      <div className="flex items-center gap-1 px-3 py-1 bg-amber-50 rounded-lg">
                        {Array.from({ length: fac.starRating.predicted }).map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-amber-500 fill-amber-500" />
                        ))}
                        <span className="text-xs text-amber-700 font-medium ml-1">{fac.starRating.label}</span>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-center mb-4">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xl font-bold text-gray-900">{fac.occupancy}/{fac.beds}</div>
                      <div className="text-xs text-gray-500">Occupancy</div>
                    </div>
                    <Tooltip content={`${careMin >= 215 ? 'Meeting' : 'Below'} 215 min target. Green ≥215, amber ≥200, red <200.`}>
                      <div className="bg-gray-50 rounded-lg p-3 cursor-help">
                        <div className={`text-xl font-bold ${careColor}`}>{careMin.toFixed(0)}</div>
                        <div className="text-xs text-gray-500">Care min/res/day</div>
                      </div>
                    </Tooltip>
                    <Tooltip content={`Agency staff as percentage of total hours. Sector benchmark is ~8%. Lower is better for cost and continuity.`}>
                      <div className="bg-gray-50 rounded-lg p-3 cursor-help">
                        <div className="text-xl font-bold text-gray-900">{fmtPct(metrics.agencyPercentage)}</div>
                        <div className="text-xs text-gray-500">Agency %</div>
                      </div>
                    </Tooltip>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Open Shifts</span>
                    <span className="font-bold text-indigo-600">{fac.marketplaceActivity?.openShifts || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-gray-500">Fill Rate</span>
                    <span className="font-bold text-emerald-600">{fmtPct(fac.marketplaceActivity?.fillRate)}</span>
                  </div>
                  <div className="flex gap-2 mt-4">
                    {['RN', 'Turnover', 'Compliance'].map((label, i) => {
                      const colors = ['bg-emerald-500', 'bg-amber-500', 'bg-emerald-500'];
                      return (
                        <div key={label} className="flex items-center gap-1.5 text-xs">
                          <div className={`w-2.5 h-2.5 rounded-full ${colors[i]}`} />
                          {label}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Float pool summary */}
          {floatPool && (
            <Card className="p-6">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
                <Truck className="w-5 h-5 text-indigo-600" /> Float Pool
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-indigo-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-indigo-700">{floatPool.totalFloatStaff}</div>
                  <div className="text-xs text-gray-500">Total Float Staff</div>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-emerald-700">{floatPool.availableToday}</div>
                  <div className="text-xs text-gray-500">Available Today</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-purple-700">{Object.keys(floatPool.byRole || {}).length}</div>
                  <div className="text-xs text-gray-500">Role Categories</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-amber-700">{floatPool.staff?.filter(s => s.isAvailableToday).length || 0}</div>
                  <div className="text-xs text-gray-500">Cross-Facility Ready</div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Staff</th>
                      <th className="px-3 py-2 text-left">Role</th>
                      <th className="px-3 py-2 text-left">Type</th>
                      <th className="px-3 py-2 text-left">Home Facility</th>
                      <th className="px-3 py-2 text-left">Available</th>
                      <th className="px-3 py-2 text-left">Last Shift</th>
                      <th className="px-3 py-2 text-center">Quick Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(floatPool.staff || []).slice(0, 8).map(s => (
                      <tr key={s.staffId} className="border-t border-gray-100 hover:bg-gray-50 group">
                        <td className="px-3 py-2 font-medium">{s.staffName}</td>
                        <td className="px-3 py-2"><span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full text-xs font-medium">{s.roleCode}</span></td>
                        <td className="px-3 py-2 capitalize">{s.employmentType}</td>
                        <td className="px-3 py-2">{s.facilityName}</td>
                        <td className="px-3 py-2">
                          {s.isAvailableToday
                            ? <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-medium"><CheckCircle className="w-3.5 h-3.5" />Yes</span>
                            : <span className="inline-flex items-center gap-1 text-gray-400 text-xs font-medium"><XCircle className="w-3.5 h-3.5" />No</span>
                          }
                        </td>
                        <td className="px-3 py-2 text-gray-500">{fmtDate(s.lastShiftDate)}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                            <Tooltip content="Approve timesheet">
                              <button
                                onClick={() => handleQuickApproveTimesheet(s.staffId)}
                                className="p-1.5 rounded-lg hover:bg-emerald-100 text-emerald-600 transition-colors"
                                aria-label={`Approve timesheet for ${s.staffName}`}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            </Tooltip>
                            <Tooltip content="Reassign shift">
                              <div className="relative">
                                <button
                                  onClick={() => setReassignDropdown(reassignDropdown === s.staffId ? null : s.staffId)}
                                  className="p-1.5 rounded-lg hover:bg-blue-100 text-blue-600 transition-colors"
                                  aria-label={`Reassign shift for ${s.staffName}`}
                                >
                                  <ArrowRight className="w-4 h-4" />
                                </button>
                                {reassignDropdown === s.staffId && (
                                  <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-30 w-48">
                                    <div className="px-3 py-1.5 text-xs font-medium text-gray-500 border-b border-gray-100">Reassign to:</div>
                                    {(floatPool.staff || []).filter(other => other.staffId !== s.staffId && other.isAvailableToday).slice(0, 5).map(other => (
                                      <button
                                        key={other.staffId}
                                        onClick={() => {
                                          showToast(`Shift reassigned to ${other.staffName}`, 'success');
                                          setReassignDropdown(null);
                                        }}
                                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50 flex items-center gap-2"
                                      >
                                        <div className="w-5 h-5 bg-indigo-100 rounded-full flex items-center justify-center text-[10px] font-bold text-indigo-700">
                                          {other.staffName?.split(' ').map(n => n[0]).join('')}
                                        </div>
                                        {other.staffName}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </Tooltip>
                            <Tooltip content="Send reminder">
                              <button
                                onClick={() => handleSendReminder(s.staffName)}
                                className="p-1.5 rounded-lg hover:bg-amber-100 text-amber-600 transition-colors"
                                aria-label={`Send reminder to ${s.staffName}`}
                              >
                                <Bell className="w-4 h-4" />
                              </button>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Staff search bar */}
          <Card className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search staff across all facilities..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            {searchQuery && floatPool?.staff && (
              <div className="mt-3 space-y-1">
                {floatPool.staff.filter(s => s.staffName.toLowerCase().includes(searchQuery.toLowerCase())).map(s => (
                  <div key={s.staffId} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-xs font-bold text-indigo-700">
                        {s.staffName.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <div className="font-medium">{s.staffName}</div>
                        <div className="text-xs text-gray-500">{s.roleCode} — {s.facilityName}</div>
                      </div>
                    </div>
                    <StatusBadge status={s.isAvailableToday ? 'active' : 'completed'} />
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          TAB 2 — Demand Engine
          ═══════════════════════════════════════════════════════════════ */}
      {activeSubTab === 'demand' && (
        <div className="space-y-6">
          {/* Per-ward demand cards */}
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-5 h-5 text-indigo-600" /> Ward Staffing Demand (Today)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(demandData?.dailyDemand?.[0]?.wards || []).map(ward => {
              const rosteredRN = Math.max(1, ward.requiredRN - Math.floor(Math.random() * 2));
              const rosteredEN = Math.max(0, ward.requiredEN - Math.floor(Math.random()));
              const rosteredPCA = Math.max(1, ward.requiredPCA - Math.floor(Math.random() * 2));
              return (
                <Card key={ward.wardId} className="p-5">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-bold text-gray-900">{ward.wardName}</h4>
                      <p className="text-xs text-gray-500">{ward.occupancy}/{ward.beds} beds occupied</p>
                    </div>
                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                      ward.acuityLevel === 'critical' ? 'bg-red-100 text-red-700' :
                      ward.acuityLevel === 'high' ? 'bg-orange-100 text-orange-700' :
                      ward.acuityLevel === 'medium' ? 'bg-amber-100 text-amber-700' :
                      'bg-emerald-100 text-emerald-700'
                    }`}>
                      {(ward.acuityLevel || 'medium').toUpperCase()}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {[
                      { role: 'RN', req: ward.requiredRN, rost: rosteredRN },
                      { role: 'EN', req: ward.requiredEN, rost: rosteredEN },
                      { role: 'PCA', req: ward.requiredPCA, rost: rosteredPCA },
                    ].map(r => (
                      <div key={r.role} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 w-10">{r.role}</span>
                        <div className="flex items-center gap-2">
                          <span className={`font-bold ${demandIndicator(r.req, r.rost)}`}>{r.rost}</span>
                          <span className="text-gray-400">/</span>
                          <span className="text-gray-600">{r.req} req</span>
                          {r.rost >= r.req
                            ? <CheckCircle className="w-4 h-4 text-emerald-500" />
                            : r.rost >= r.req * 0.8
                              ? <AlertTriangle className="w-4 h-4 text-amber-500" />
                              : <XCircle className="w-4 h-4 text-red-500" />
                          }
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                    Total required: <span className="font-bold text-gray-700">{ward.totalRequired}</span> staff this shift
                  </div>
                </Card>
              );
            })}
          </div>

          {/* 7-day demand timeline */}
          <Card className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-indigo-600" /> 7-Day Demand vs Supply
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-center">RN Req</th>
                    <th className="px-3 py-2 text-center">EN Req</th>
                    <th className="px-3 py-2 text-center">PCA Req</th>
                    <th className="px-3 py-2 text-center">Total</th>
                    <th className="px-3 py-2 text-center">Supply</th>
                    <th className="px-3 py-2 text-center">Gap</th>
                  </tr>
                </thead>
                <tbody>
                  {(demandData?.dailyDemand || []).map((day, idx) => {
                    const supply = Math.round(day.demand.totalRequired * (0.85 + Math.random() * 0.2));
                    const gap = day.demand.totalRequired - supply;
                    const rowBg = gap > 2 ? 'bg-red-50' : gap > 0 ? 'bg-amber-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50';
                    return (
                      <tr key={day.date} className={`${rowBg} border-t border-gray-100`}>
                        <td className="px-3 py-2 font-medium">{fmtDate(day.date)}</td>
                        <td className="px-3 py-2 text-center">{day.demand.requiredRN}</td>
                        <td className="px-3 py-2 text-center">{day.demand.requiredEN}</td>
                        <td className="px-3 py-2 text-center">{day.demand.requiredPCA}</td>
                        <td className="px-3 py-2 text-center font-bold">{day.demand.totalRequired}</td>
                        <td className="px-3 py-2 text-center">{supply}</td>
                        <td className={`px-3 py-2 text-center font-bold ${gap > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          {gap > 0 ? `-${gap}` : gap === 0 ? '0' : `+${Math.abs(gap)}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Acuity heatmap */}
          {heatmapData && (
            <Card className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Thermometer className="w-5 h-5 text-indigo-600" /> Acuity Heatmap
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Ward</th>
                      <th className="px-3 py-2 text-center">Occupancy</th>
                      <th className="px-3 py-2 text-center">High Need %</th>
                      <th className="px-3 py-2 text-center">AM</th>
                      <th className="px-3 py-2 text-center">PM</th>
                      <th className="px-3 py-2 text-center">NIGHT</th>
                      <th className="px-3 py-2 text-center">Acuity Level</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(heatmapData.heatmap || []).map(ward => (
                      <tr key={ward.wardId} className="border-t border-gray-100">
                        <td className="px-3 py-2 font-medium">{ward.wardName}</td>
                        <td className="px-3 py-2 text-center">{ward.occupancy}/{ward.beds} ({ward.occupancyRate}%)</td>
                        <td className="px-3 py-2 text-center font-medium">{ward.highNeedRatio}%</td>
                        {['AM', 'PM', 'NIGHT'].map(shift => (
                          <td key={shift} className="px-3 py-2 text-center">
                            <span className={`inline-block px-2 py-1 rounded text-xs font-bold ${acuityColor(ward.acuityLevel)}`}>
                              {ward.shiftDemand?.[shift]?.total || 0}
                            </span>
                          </td>
                        ))}
                        <td className="px-3 py-2 text-center">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase ${acuityColor(ward.acuityLevel)}`}>
                            {ward.acuityLevel}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-4 mt-4 text-xs">
                {[
                  { label: 'Low', cls: 'bg-emerald-200' },
                  { label: 'Medium', cls: 'bg-amber-300' },
                  { label: 'High', cls: 'bg-orange-400' },
                  { label: 'Critical', cls: 'bg-red-500' },
                ].map(l => (
                  <div key={l.label} className="flex items-center gap-1.5">
                    <div className={`w-3 h-3 rounded ${l.cls}`} />
                    <span className="text-gray-600">{l.label}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Demand alerts */}
          {demandAlerts.length > 0 && (
            <Card className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Bell className="w-5 h-5 text-red-500" /> Demand Change Alerts
              </h3>
              <div className="space-y-2">
                {demandAlerts.map((alert, idx) => (
                  <div key={idx} className={`flex items-start gap-3 p-3 rounded-lg ${
                    alert.severity === 'high' ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'
                  }`}>
                    <AlertTriangle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${alert.severity === 'high' ? 'text-red-500' : 'text-amber-500'}`} />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{alert.wardName}</p>
                      <p className="text-xs text-gray-600">{alert.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          TAB 3 — Leave Management
          ═══════════════════════════════════════════════════════════════ */}
      {activeSubTab === 'leave' && (
        <div className="space-y-6">
          {/* Actions bar */}
          <div className="flex flex-wrap gap-3">
            <button onClick={() => setShowLeaveModal(true)} className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition-colors">
              <Calendar className="w-4 h-4" /> New Leave Request
            </button>
            <button onClick={() => setShowBlackoutModal(true)} className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors">
              <Shield className="w-4 h-4" /> Manage Blackout Periods
            </button>
          </div>

          {/* Pending requests table */}
          <Card className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Pending Requests ({pendingLeave.length})</h3>
            {pendingLeave.length === 0 ? (
              <EmptyState
                icon={Calendar}
                title="No Pending Leave Requests"
                description="All leave requests have been processed. New requests will appear here."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Staff</th>
                      <th className="px-3 py-2 text-left">Type</th>
                      <th className="px-3 py-2 text-left">Dates</th>
                      <th className="px-3 py-2 text-center">Hours</th>
                      <th className="px-3 py-2 text-center">Med Cert</th>
                      <th className="px-3 py-2 text-left">Requested</th>
                      <th className="px-3 py-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingLeave.map(req => (
                      <tr key={req.id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium">{req.staffName}</td>
                        <td className="px-3 py-2 capitalize">{(req.leaveType || '').replace(/_/g, ' ')}</td>
                        <td className="px-3 py-2">{fmtDate(req.startDate)} — {fmtDate(req.endDate)}</td>
                        <td className="px-3 py-2 text-center">{req.hoursDeducted}h</td>
                        <td className="px-3 py-2 text-center">
                          {req.medicalCertificateAttached
                            ? <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" />
                            : <span className="text-gray-400">—</span>
                          }
                        </td>
                        <td className="px-3 py-2 text-gray-500">{fmtDate(req.requestedAt)}</td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => handleApproveLeave(req.id)} className="px-2.5 py-1 bg-emerald-600 text-white rounded-md text-xs font-medium hover:bg-emerald-700">
                              Approve
                            </button>
                            <button onClick={() => handleDeclineLeave(req.id)} className="px-2.5 py-1 bg-red-600 text-white rounded-md text-xs font-medium hover:bg-red-700">
                              Decline
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Team leave calendar */}
          {leaveCalendar && (
            <Card className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-600" /> Team Leave Calendar (This Month)
              </h3>
              <div className="grid grid-cols-7 gap-1">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                  <div key={day} className="text-xs font-medium text-gray-500 text-center py-1">{day}</div>
                ))}
                {(() => {
                  const now = new Date();
                  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
                  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
                  const startOffset = (firstDay.getDay() + 6) % 7;
                  const cells = [];
                  for (let i = 0; i < startOffset; i++) cells.push(<div key={`pad-${i}`} />);
                  for (let d = 1; d <= daysInMonth; d++) {
                    const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                    const evts = leaveCalendar.calendar?.[dateStr] || [];
                    const isToday = d === now.getDate();
                    cells.push(
                      <div key={d} className={`border rounded-lg p-1 min-h-[60px] text-xs ${isToday ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200'}`}>
                        <div className={`font-medium mb-0.5 ${isToday ? 'text-indigo-700' : 'text-gray-700'}`}>{d}</div>
                        {evts.slice(0, 2).map((e, i) => (
                          <div key={i} className="truncate text-[10px] bg-amber-100 text-amber-800 rounded px-1 mb-0.5">{e.staffName?.split(' ')[0]}</div>
                        ))}
                        {evts.length > 2 && <div className="text-[10px] text-gray-400">+{evts.length - 2} more</div>}
                      </div>
                    );
                  }
                  return cells;
                })()}
              </div>
            </Card>
          )}

          {/* Leave balances */}
          <Card className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Leave Balances (Hours)</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Staff</th>
                    {['Annual', 'Personal', 'Long Service', 'Compassionate', 'Study', 'TOIL'].map(t => (
                      <th key={t} className="px-3 py-2 text-center">{t}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leaveBalances.map(lb => (
                    <tr key={lb.staffId} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium">{lb.staffName}</td>
                      {(lb.balances || []).map(b => (
                        <td key={b.leaveType} className="px-3 py-2 text-center">
                          <span className={`font-medium ${b.available > 40 ? 'text-emerald-600' : b.available > 0 ? 'text-amber-600' : 'text-red-600'}`}>
                            {b.available}
                          </span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Leave liability */}
          {leaveLiability && (
            <Card className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-indigo-600" /> Leave Liability Summary
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-red-700">{fmt$(leaveLiability.totalLiability)}</div>
                  <div className="text-xs text-gray-500">Total Liability</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-gray-700">{leaveLiability.staffCount}</div>
                  <div className="text-xs text-gray-500">Staff with Balance</div>
                </div>
                {Object.entries(leaveLiability.byLeaveType || {}).slice(0, 2).map(([type, info]) => (
                  <div key={type} className="bg-gray-50 rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-gray-700">{fmt$(info.totalDollars)}</div>
                    <div className="text-xs text-gray-500">{info.label}</div>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Staff</th>
                      <th className="px-3 py-2 text-right">Rate/hr</th>
                      <th className="px-3 py-2 text-right">Total Liability</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(leaveLiability.staffLiabilities || []).map(sl => (
                      <tr key={sl.staffId} className="border-t border-gray-100">
                        <td className="px-3 py-2 font-medium">{sl.staffName}</td>
                        <td className="px-3 py-2 text-right">{fmt$(sl.hourlyRate)}</td>
                        <td className="px-3 py-2 text-right font-bold text-red-600">{fmt$(sl.totalLiability)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Blackout periods */}
          <Card className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-500" /> Blackout Periods
            </h3>
            {blackoutPeriods.length === 0 ? (
              <EmptyState
                icon={Shield}
                title="No Blackout Periods"
                description="No blackout periods are configured. Add periods when leave should be restricted."
              />
            ) : (
              <div className="space-y-2">
                {blackoutPeriods.map(bp => (
                  <div key={bp.id} className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-red-800">{bp.reason}</p>
                      <p className="text-xs text-red-600">{fmtDate(bp.startDate)} — {fmtDate(bp.endDate)}</p>
                    </div>
                    <span className="text-xs text-gray-500">by {bp.createdBy}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          TAB 4 — Timesheets
          ═══════════════════════════════════════════════════════════════ */}
      {activeSubTab === 'timesheets' && (
        <div className="space-y-6">
          {/* Pay period selector */}
          <Card className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Pay Period:</label>
                <input type="date" value={payPeriodStart} onChange={e => setPayPeriodStart(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                <span className="text-gray-400">to</span>
                <input type="date" value={payPeriodEnd} onChange={e => setPayPeriodEnd(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filter by staff name..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <button
                onClick={handleBulkApproveTimesheets}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
              >
                <CheckCircle className="w-4 h-4" /> Bulk Approve
              </button>
            </div>
          </Card>

          {/* Pay period summary */}
          {payPeriodSummary && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <MetricCard label="Total Hours" value={payPeriodSummary.totalHours} icon={Clock} color="indigo" />
              <MetricCard label="Ordinary Hours" value={payPeriodSummary.totalOrdinaryHours} icon={Timer} color="blue" />
              <MetricCard label="Overtime Hours" value={payPeriodSummary.totalOvertimeHours} icon={Zap} color="amber" />
              <MetricCard label="Timesheets" value={payPeriodSummary.timesheetCount} icon={FileText} color="purple" />
              <MetricCard label="Approved" value={payPeriodSummary.approved} icon={CheckCircle} color="emerald" sub={`${payPeriodSummary.pending} pending`} />
            </div>
          )}

          {/* Timesheet table */}
          <Card className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Timesheets ({filteredTimesheets.length})</h3>
            {filteredTimesheets.length === 0 ? (
              <EmptyState
                icon={Clock}
                title="No Timesheets Found"
                description="No timesheets match this period or search. Generate timesheets from clock records first."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Staff</th>
                      <th className="px-3 py-2 text-center">Clock In</th>
                      <th className="px-3 py-2 text-center">Clock Out</th>
                      <th className="px-3 py-2 text-center">Ordinary</th>
                      <th className="px-3 py-2 text-center">Overtime</th>
                      <th className="px-3 py-2 text-center">Variance</th>
                      <th className="px-3 py-2 text-center">Status</th>
                      <th className="px-3 py-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTimesheets.map(ts => {
                      const variance = ts.varianceMinutes || 0;
                      const varColor = variance > 30 ? 'text-red-600 bg-red-50' : variance > 10 ? 'text-amber-600 bg-amber-50' : 'text-gray-600';
                      return (
                        <tr key={ts.id} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium">{ts.staffName || ts.staffId}</td>
                          <td className="px-3 py-2 text-center text-gray-500">{fmtTime(ts.clockInTime)}</td>
                          <td className="px-3 py-2 text-center text-gray-500">{fmtTime(ts.clockOutTime)}</td>
                          <td className="px-3 py-2 text-center">{(ts.ordinaryHours || 0).toFixed(1)}h</td>
                          <td className="px-3 py-2 text-center">{(ts.overtimeHours || 0).toFixed(1)}h</td>
                          <td className="px-3 py-2 text-center">
                            {variance > 0 ? (
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${varColor}`}>
                                {variance}min
                              </span>
                            ) : <span className="text-gray-400">—</span>}
                          </td>
                          <td className="px-3 py-2 text-center"><StatusBadge status={ts.status} /></td>
                          <td className="px-3 py-2 text-center">
                            {ts.status !== 'approved' && ts.status !== 'exported' && (
                              <button
                                onClick={() => handleApproveTimesheet(ts.id)}
                                className="text-xs text-emerald-600 hover:text-emerald-800 font-medium"
                              >
                                Approve
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          TAB 5 — Marketplace
          ═══════════════════════════════════════════════════════════════ */}
      {activeSubTab === 'marketplace' && (
        <div className="space-y-6">
          {/* Analytics overview */}
          {marketplaceAnalytics && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <MetricCard label="Fill Rate" value={fmtPct(marketplaceAnalytics.fillRate)} icon={Target} color="emerald" />
              <MetricCard label="Avg Time to Fill" value={`${marketplaceAnalytics.avgTimeToFillHours}h`} icon={Timer} color="blue" />
              <MetricCard label="Agency Averted" value={marketplaceAnalytics.agencyShiftsAverted} icon={Shield} color="purple" />
              <MetricCard label="Savings" value={fmt$(marketplaceAnalytics.totalSavingsVsAgency)} icon={DollarSign} color="emerald" />
              <MetricCard label="Open Shifts" value={marketplaceAnalytics.open} icon={Globe} color="amber" />
            </div>
          )}

          {/* Open shifts grid */}
          <Card className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-indigo-600" /> Open Shifts
            </h3>
            {openMarketplace.length === 0 ? (
              <EmptyState
                icon={Globe}
                title="No Open Shifts"
                description="All marketplace shifts have been filled or there are no shifts currently posted."
                action={<button className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">Post a Shift</button>}
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Facility</th>
                      <th className="px-3 py-2 text-left">Shift ID</th>
                      <th className="px-3 py-2 text-center">Tier</th>
                      <th className="px-3 py-2 text-center">Applicants</th>
                      <th className="px-3 py-2 text-left">Posted</th>
                      <th className="px-3 py-2 text-center">Status</th>
                      <th className="px-3 py-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openMarketplace.map(shift => (
                      <tr key={shift.id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium">{shift.facilityName || shift.facilityId}</td>
                        <td className="px-3 py-2 text-xs text-gray-500 font-mono">{(shift.shiftId || '').slice(0, 30)}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            shift.currentTier === 'casual_pool' ? 'bg-blue-100 text-blue-700' :
                            shift.currentTier === 'float_pool' ? 'bg-purple-100 text-purple-700' :
                            shift.currentTier === 'pt_extras' ? 'bg-emerald-100 text-emerald-700' :
                            shift.currentTier === 'agency' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {(shift.currentTier || '').replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center font-bold">{shift.applicants?.length || 0}</td>
                        <td className="px-3 py-2 text-gray-500">{fmtDate(shift.postedAt)}</td>
                        <td className="px-3 py-2 text-center"><StatusBadge status={shift.status} /></td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => setSelectedMarketplaceShift(shift)}
                            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                          >
                            View Applicants
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Tier breakdown */}
          {marketplaceAnalytics?.tierBreakdown && (
            <Card className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Layers className="w-5 h-5 text-indigo-600" /> Tier Breakdown
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {marketplaceAnalytics.tierBreakdown.map(tier => (
                  <div key={tier.tier} className="bg-gray-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-gray-900">{tier.count}</div>
                    <div className="text-xs text-gray-500 capitalize">{tier.label}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Agency vs internal comparison */}
          <Card className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-600" /> Agency vs Internal Cost Comparison
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-emerald-50 rounded-lg p-4 text-center">
                <div className="text-xs text-gray-500 mb-1">Internal Staff Cost/Shift</div>
                <div className="text-2xl font-bold text-emerald-700">$285</div>
                <div className="text-xs text-emerald-600 mt-1">8hr AM shift (RN Lvl 1)</div>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <div className="text-xs text-gray-500 mb-1">Agency Cost/Shift</div>
                <div className="text-2xl font-bold text-red-700">$480</div>
                <div className="text-xs text-red-600 mt-1">8hr AM shift (RN Agency)</div>
              </div>
              <div className="bg-indigo-50 rounded-lg p-4 text-center">
                <div className="text-xs text-gray-500 mb-1">Savings per Shift</div>
                <div className="text-2xl font-bold text-indigo-700">$195</div>
                <div className="text-xs text-indigo-600 mt-1">40.6% cost reduction</div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          TAB 6 — Payroll
          ═══════════════════════════════════════════════════════════════ */}
      {activeSubTab === 'payroll' && (
        <div className="space-y-6">
          {/* Tab-level export bar */}
          <div className="flex items-center justify-end gap-2 print:hidden">
            <button
              onClick={() => handleExport(
                (payRunPreview?.staffSummary || []).map(s => ({ employee: s.staffName, shifts: s.shifts, totalHours: s.totalHours, estimatedGross: s.estimatedGross })),
                'payroll_preview'
              )}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4" /> Download CSV
            </button>
            <button
              onClick={() => handlePrint('Payroll Report')}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <Printer className="w-4 h-4" /> Print Report
            </button>
          </div>

          {/* Pay run preview */}
          {payRunPreview && (
            <Card className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-indigo-600" /> Pay Run Preview
                </h3>
                <div className="flex gap-2">
                  {['Xero', 'MYOB', 'KeyPay', 'ADP', 'CSV'].map(fmt => (
                    <button key={fmt} className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors">
                      <Download className="w-3 h-3" /> {fmt}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-indigo-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-indigo-700">{payRunPreview.staffCount}</div>
                  <div className="text-xs text-gray-500">Employees</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-700">{payRunPreview.totalHours}</div>
                  <div className="text-xs text-gray-500">Total Hours</div>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-emerald-700">{fmt$(payRunPreview.estimatedGross)}</div>
                  <div className="text-xs text-gray-500">Est. Gross Pay</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-purple-700">{payRunPreview.totalShifts}</div>
                  <div className="text-xs text-gray-500">Total Shifts</div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Employee</th>
                      <th className="px-3 py-2 text-center">Shifts</th>
                      <th className="px-3 py-2 text-center">Total Hours</th>
                      <th className="px-3 py-2 text-right">Est. Gross</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(payRunPreview.staffSummary || []).map(s => (
                      <tr key={s.staffId} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium">{s.staffName}</td>
                        <td className="px-3 py-2 text-center">{s.shifts}</td>
                        <td className="px-3 py-2 text-center">{s.totalHours}</td>
                        <td className="px-3 py-2 text-right font-medium">{fmt$(s.estimatedGross)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 font-bold">
                    <tr className="border-t-2 border-gray-300">
                      <td className="px-3 py-2">TOTALS</td>
                      <td className="px-3 py-2 text-center">{payRunPreview.totalShifts}</td>
                      <td className="px-3 py-2 text-center">{payRunPreview.totalHours}</td>
                      <td className="px-3 py-2 text-right">{fmt$(payRunPreview.estimatedGross)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </Card>
          )}

          {/* Pay run history */}
          <Card className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-indigo-600" /> Pay Run History
            </h3>
            {payRunHistory.length === 0 ? (
              <EmptyState
                icon={FileSpreadsheet}
                title="No Pay Run History"
                description="No pay runs have been generated yet for this facility. Process your first pay run above."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Pay Run ID</th>
                      <th className="px-3 py-2 text-left">Period</th>
                      <th className="px-3 py-2 text-center">Employees</th>
                      <th className="px-3 py-2 text-right">Gross</th>
                      <th className="px-3 py-2 text-right">Super</th>
                      <th className="px-3 py-2 text-center">Status</th>
                      <th className="px-3 py-2 text-left">Generated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payRunHistory.map(pr => (
                      <tr key={pr.id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2 text-xs font-mono text-gray-600">{pr.id?.slice(0, 20)}</td>
                        <td className="px-3 py-2">{fmtDate(pr.payPeriod?.start)} — {fmtDate(pr.payPeriod?.end)}</td>
                        <td className="px-3 py-2 text-center">{pr.lineItemCount}</td>
                        <td className="px-3 py-2 text-right font-medium">{fmt$(pr.totalGross)}</td>
                        <td className="px-3 py-2 text-right">{fmt$(pr.totalSuper)}</td>
                        <td className="px-3 py-2 text-center"><StatusBadge status={pr.status} /></td>
                        <td className="px-3 py-2 text-gray-500">{fmtDate(pr.generatedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Award rate reference */}
          <Card className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-indigo-600" /> Award Rate Reference (MA000018 — Nurses Award)
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Classification</th>
                    <th className="px-3 py-2 text-right">Ordinary</th>
                    <th className="px-3 py-2 text-right"><Tooltip content="Saturday penalty: 150% of ordinary rate per MA000018"><span className="cursor-help">Saturday</span></Tooltip></th>
                    <th className="px-3 py-2 text-right"><Tooltip content="Sunday penalty: 175% of ordinary rate per MA000018"><span className="cursor-help">Sunday</span></Tooltip></th>
                    <th className="px-3 py-2 text-right"><Tooltip content="Public holiday penalty: 250% of ordinary rate per MA000018"><span className="cursor-help">Public Holiday</span></Tooltip></th>
                    <th className="px-3 py-2 text-right"><Tooltip content="Night shift loading: 125% of ordinary rate per MA000018"><span className="cursor-help">Night (125%)</span></Tooltip></th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { cls: 'RN Level 1.1', base: 35.71 },
                    { cls: 'RN Level 2.1', base: 39.12 },
                    { cls: 'EN Level 1.1', base: 30.89 },
                    { cls: 'PCA Level 2.1', base: 27.14 },
                    { cls: 'PCA Level 3.1', base: 28.47 },
                  ].map(r => (
                    <tr key={r.cls} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium">{r.cls}</td>
                      <td className="px-3 py-2 text-right">{fmt$(r.base)}</td>
                      <td className="px-3 py-2 text-right">{fmt$(r.base * 1.5)}</td>
                      <td className="px-3 py-2 text-right">{fmt$(r.base * 1.75)}</td>
                      <td className="px-3 py-2 text-right">{fmt$(r.base * 2.5)}</td>
                      <td className="px-3 py-2 text-right">{fmt$(r.base * 1.25)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          TAB 7 — Compliance
          ═══════════════════════════════════════════════════════════════ */}
      {activeSubTab === 'compliance' && (
        <div className="space-y-6">
          {/* Tab-level export bar */}
          <div className="flex items-center justify-end gap-2 print:hidden">
            <button
              onClick={() => handleExport(
                [
                  { metric: 'Care Min/Res/Day', achieved: careMinutesReport?.careMinutes?.achievedPerResidentPerDay, target: careMinutesReport?.careMinutes?.targetPerResidentPerDay, compliant: careMinutesReport?.careMinutes?.compliant },
                  { metric: 'RN Min/Res/Day', achieved: careMinutesReport?.rnMinutes?.achievedPerResidentPerDay, target: careMinutesReport?.rnMinutes?.targetPerResidentPerDay, compliant: careMinutesReport?.rnMinutes?.compliant },
                  { metric: 'RN Coverage Rate', achieved: rnDeclaration?.coverageRate, target: '100%', compliant: rnDeclaration?.compliant },
                  { metric: 'Star Rating', achieved: starRating?.predictedStarRating, target: '5', compliant: starRating?.predictedStarRating >= 4 },
                ],
                'compliance_report'
              )}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4" /> Download CSV
            </button>
            <button
              onClick={() => handlePrint('Compliance Report')}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <Printer className="w-4 h-4" /> Print Report
            </button>
          </div>

          {/* Care minutes compliance */}
          {careMinutesReport && (
            <Card className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Stethoscope className="w-5 h-5 text-indigo-600" /> Care Minutes Compliance
                </h3>
                <div className="flex gap-2 print:hidden">
                  <button className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700">
                    <Upload className="w-3 h-3" /> Submit to GPMS
                  </button>
                  <button
                    onClick={() => handleExport(
                      [{ careMinAchieved: careMinutesReport.careMinutes?.achievedPerResidentPerDay, careMinTarget: careMinutesReport.careMinutes?.targetPerResidentPerDay, rnMinAchieved: careMinutesReport.rnMinutes?.achievedPerResidentPerDay, rnMinTarget: careMinutesReport.rnMinutes?.targetPerResidentPerDay, occupancy: careMinutesReport.occupancy, totalBeds: careMinutesReport.totalBeds }],
                      'care_minutes_detail'
                    )}
                    className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50"
                  >
                    <Download className="w-3 h-3" /> Export
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Tooltip content={`${careMinutesReport.careMinutes?.compliant ? 'Meeting' : 'Below'} the ${careMinutesReport.careMinutes?.targetPerResidentPerDay} min/res/day AN-ACC target. Current: ${careMinutesReport.careMinutes?.achievedPerResidentPerDay} min.`} position="bottom">
                  <div className={`rounded-lg p-4 text-center cursor-help ${careMinutesReport.careMinutes?.compliant ? 'bg-emerald-50' : 'bg-red-50'}`}>
                    <div className={`text-3xl font-bold ${careMinutesReport.careMinutes?.compliant ? 'text-emerald-700' : 'text-red-700'}`}>
                      {careMinutesReport.careMinutes?.achievedPerResidentPerDay}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Care min/res/day</div>
                    <div className="text-xs mt-1 font-medium">(Target: {careMinutesReport.careMinutes?.targetPerResidentPerDay})</div>
                  </div>
                </Tooltip>
                <Tooltip content={`${careMinutesReport.rnMinutes?.compliant ? 'Compliant' : 'Non-compliant'} with RN minutes requirement. Achieved ${careMinutesReport.rnMinutes?.achievedPerResidentPerDay} of ${careMinutesReport.rnMinutes?.targetPerResidentPerDay} target.`} position="bottom">
                  <div className={`rounded-lg p-4 text-center cursor-help ${careMinutesReport.rnMinutes?.compliant ? 'bg-emerald-50' : 'bg-red-50'}`}>
                    <div className={`text-3xl font-bold ${careMinutesReport.rnMinutes?.compliant ? 'text-emerald-700' : 'text-red-700'}`}>
                      {careMinutesReport.rnMinutes?.achievedPerResidentPerDay}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">RN min/res/day</div>
                    <div className="text-xs mt-1 font-medium">(Target: {careMinutesReport.rnMinutes?.targetPerResidentPerDay})</div>
                  </div>
                </Tooltip>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-gray-700">{careMinutesReport.occupancy}/{careMinutesReport.totalBeds}</div>
                  <div className="text-xs text-gray-500 mt-1">Occupancy</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-gray-700">{fmtPct(careMinutesReport.occupancyRate)}</div>
                  <div className="text-xs text-gray-500 mt-1">Occupancy Rate</div>
                </div>
              </div>
              {/* Daily chart row */}
              <div className="flex items-end gap-1 h-24 mt-2">
                {Array.from({ length: 14 }).map((_, i) => {
                  const val = 195 + Math.random() * 40;
                  const met = val >= 215;
                  const height = Math.max(20, (val / 250) * 100);
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center">
                      <div
                        className={`w-full rounded-t ${met ? 'bg-emerald-400' : 'bg-red-400'}`}
                        style={{ height: `${height}%` }}
                        title={`Day ${i + 1}: ${val.toFixed(0)} min`}
                      />
                      <div className="text-[9px] text-gray-400 mt-1">{i + 1}</div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-4 mt-2 text-xs">
                <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-emerald-400" />Meeting 215 target</div>
                <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-red-400" />Below target</div>
              </div>
            </Card>
          )}

          {/* RN 24/7 coverage */}
          {rnDeclaration && (
            <Card className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <HeartPulse className="w-5 h-5 text-indigo-600" /> RN 24/7 Coverage Declaration
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className={`rounded-lg p-3 text-center ${rnDeclaration.compliant ? 'bg-emerald-50' : 'bg-red-50'}`}>
                  <div className={`text-xl font-bold ${rnDeclaration.compliant ? 'text-emerald-700' : 'text-red-700'}`}>
                    {rnDeclaration.compliant ? 'COMPLIANT' : 'GAPS FOUND'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Declaration Status</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-gray-700">{rnDeclaration.coveredSlots}/{rnDeclaration.totalShiftSlots}</div>
                  <div className="text-xs text-gray-500 mt-1">Slots Covered</div>
                </div>
                <Tooltip content={`RN coverage across all shift slots: ${rnDeclaration.coveredSlots} of ${rnDeclaration.totalShiftSlots} covered. Regulation requires 24/7 RN presence.`} position="bottom">
                  <div className="bg-gray-50 rounded-lg p-3 text-center cursor-help">
                    <div className="text-xl font-bold text-gray-700">{fmtPct(rnDeclaration.coverageRate)}</div>
                    <div className="text-xs text-gray-500 mt-1">Coverage Rate</div>
                  </div>
                </Tooltip>
                <div className={`rounded-lg p-3 text-center ${rnDeclaration.gapSlots > 0 ? 'bg-red-50' : 'bg-emerald-50'}`}>
                  <div className={`text-xl font-bold ${rnDeclaration.gapSlots > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{rnDeclaration.gapSlots}</div>
                  <div className="text-xs text-gray-500 mt-1">Gap Slots</div>
                </div>
              </div>
              {rnDeclaration.gaps?.length > 0 && (
                <div className="space-y-2 mb-4">
                  <h4 className="text-sm font-bold text-red-700">Coverage Gaps:</h4>
                  {rnDeclaration.gaps.map((gap, i) => (
                    <div key={i} className="flex items-center gap-3 p-2 bg-red-50 rounded-lg text-sm">
                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <span className="font-medium">{fmtDate(gap.date)}</span>
                      <span className="text-red-600">{gap.shift} shift</span>
                      <span className="text-gray-500 text-xs">{gap.reason}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600 italic">
                {rnDeclaration.declarationStatement}
              </div>
            </Card>
          )}

          {/* NQIP indicators */}
          {nqipIndicators && (
            <Card className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-indigo-600" /> NQIP Workforce Indicators (Q{nqipIndicators.reportingPeriod?.quarter} {nqipIndicators.reportingPeriod?.year})
                </h3>
                <button
                  onClick={() => handleExport(
                    Object.entries(nqipIndicators.staffingIndicators || {}).map(([key, val]) => ({
                      indicator: key.replace(/([A-Z])/g, ' $1').trim(),
                      value: val,
                      benchmark: nqipIndicators.benchmarks?.[{ totalCareMinutesPerResidentPerDay: 'sectorAvgCareMinutes', rnMinutesPerResidentPerDay: 'sectorAvgRNMinutes', agencyUsePercentage: 'sectorAvgAgencyUse', staffTurnoverRate: 'sectorAvgTurnover' }[key]] || 'N/A',
                    })),
                    'nqip_indicators'
                  )}
                  className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50 print:hidden"
                >
                  <Download className="w-3 h-3" /> CSV
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {Object.entries(nqipIndicators.staffingIndicators || {}).map(([key, val]) => {
                  const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
                  const benchKey = {
                    totalCareMinutesPerResidentPerDay: 'sectorAvgCareMinutes',
                    rnMinutesPerResidentPerDay: 'sectorAvgRNMinutes',
                    agencyUsePercentage: 'sectorAvgAgencyUse',
                    staffTurnoverRate: 'sectorAvgTurnover',
                  }[key];
                  const benchmark = nqipIndicators.benchmarks?.[benchKey];
                  return (
                    <div key={key} className="bg-gray-50 rounded-lg p-3">
                      <div className="text-xs text-gray-500 mb-1 truncate" title={label}>{label}</div>
                      <div className="text-xl font-bold text-gray-900">
                        {typeof val === 'number' ? (key.includes('Percentage') || key.includes('Rate') || key.includes('Compliance') ? fmtPct(val) : val.toFixed(1)) : val}
                      </div>
                      {benchmark !== undefined && (
                        <div className="text-[10px] text-gray-400 mt-0.5">Sector avg: {benchmark}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Star rating prediction */}
          {starRating && (
            <Card className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-500" /> Star Rating Staffing Component Prediction
              </h3>
              <div className="flex items-center gap-6 mb-4">
                <Tooltip content={`Predicted star rating based on care minutes delivery, RN coverage, and staffing ratios. Score: ${starRating.predictedStarRating}/5.`} position="bottom">
                  <div className="flex items-center gap-1 cursor-help">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`w-8 h-8 ${i < starRating.predictedStarRating ? 'text-amber-500 fill-amber-500' : 'text-gray-300'}`} />
                    ))}
                  </div>
                </Tooltip>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{starRating.predictedStarRating}/5</div>
                  <div className="text-sm text-gray-500">{starRating.ratingLabel}</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-gray-700">{starRating.careMinutesAchieved}</div>
                  <div className="text-xs text-gray-500">Care Min Achieved</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-gray-700">{starRating.rnMinutesAchieved}</div>
                  <div className="text-xs text-gray-500">RN Min Achieved</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className={`text-lg font-bold ${starRating.rnCoverage24_7 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {starRating.rnCoverage24_7 ? 'Yes' : 'No'}
                  </div>
                  <div className="text-xs text-gray-500">RN 24/7 Coverage</div>
                </div>
              </div>
              {starRating.recommendations?.length > 0 && (
                <div className="space-y-1.5">
                  <h4 className="text-sm font-bold text-gray-700">Recommendations:</h4>
                  {starRating.recommendations.map((rec, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <ArrowRight className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                      {rec}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          TAB 8 — Analytics
          ═══════════════════════════════════════════════════════════════ */}
      {activeSubTab === 'analytics' && (
        <div className="space-y-6">
          {/* Tab-level export bar */}
          <div className="flex items-center justify-end gap-2 print:hidden">
            <button
              onClick={() => {
                const rows = (multiFacility?.facilities || []).map(fac => {
                  const m = fac.operationalMetrics || {};
                  return { facility: fac.facilityName, beds: fac.beds, occupancy: fac.occupancyRate, staff: m.totalStaff, labourCostPerBed: m.labourCostPerBedDay, agencyPct: m.agencyPercentage, otPct: m.overtimePercentage, careMin: m.careMinutesPerResidentPerDay, turnover: m.turnoverRateAnnual, starRating: fac.starRating?.predicted };
                });
                handleExport(rows, 'analytics_multi_facility');
              }}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4" /> Download CSV
            </button>
            <button
              onClick={() => handlePrint('Analytics Report')}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
            >
              <Printer className="w-4 h-4" /> Print Report
            </button>
          </div>

          {/* KPI cards row */}
          {analyticsMetrics && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <MetricCard label="Labour Cost/Bed Day" value={fmt$(analyticsMetrics.labourCostPerBedDay)} icon={DollarSign} color="indigo" trend="+2.3% vs last month" trendDir="up" />
              <Tooltip content="Agency hours as % of total staffing hours. Benchmark: <10%. Higher values increase cost and reduce care continuity.">
                <div><MetricCard label="Agency Spend %" value={fmtPct(analyticsMetrics.agencyPercentage)} icon={Briefcase} color="amber" trend={analyticsMetrics.agencyPercentage > 10 ? 'Above benchmark' : 'Below benchmark'} trendDir={analyticsMetrics.agencyPercentage > 10 ? 'down' : 'up'} /></div>
              </Tooltip>
              <Tooltip content="Overtime as % of total hours. High OT signals understaffing or roster inefficiency.">
                <div><MetricCard label="Overtime %" value={fmtPct(analyticsMetrics.overtimePercentage)} icon={Zap} color="orange" /></div>
              </Tooltip>
              <Tooltip content="Annualised staff turnover rate. Sector avg is ~25%. Predictive model uses tenure, shift patterns, and engagement.">
                <div><MetricCard label="Turnover Rate" value={fmtPct(analyticsMetrics.turnoverRateAnnual)} icon={TrendingDown} color="red" sub="Annualised" /></div>
              </Tooltip>
              <MetricCard label="Care Minutes" value={`${analyticsMetrics.careMinutesPerResidentPerDay?.toFixed(0)}`} icon={HeartPulse} color="emerald" sub="min/res/day" />
            </div>
          )}

          {/* Multi-facility comparison */}
          <Card className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" /> Multi-Facility Comparison
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Facility</th>
                    <th className="px-3 py-2 text-center">Beds</th>
                    <th className="px-3 py-2 text-center">Occupancy %</th>
                    <th className="px-3 py-2 text-center">Staff</th>
                    <th className="px-3 py-2 text-center">Labour Cost/Bed</th>
                    <th className="px-3 py-2 text-center">Agency %</th>
                    <th className="px-3 py-2 text-center">OT %</th>
                    <th className="px-3 py-2 text-center">Care Min</th>
                    <th className="px-3 py-2 text-center">Turnover</th>
                    <th className="px-3 py-2 text-center">Star</th>
                  </tr>
                </thead>
                <tbody>
                  {(multiFacility?.facilities || []).map(fac => {
                    const m = fac.operationalMetrics || {};
                    return (
                      <tr key={fac.facilityId} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium">{fac.facilityName}</td>
                        <td className="px-3 py-2 text-center">{fac.beds}</td>
                        <td className="px-3 py-2 text-center">{fmtPct(fac.occupancyRate)}</td>
                        <td className="px-3 py-2 text-center">{m.totalStaff || 0}</td>
                        <td className="px-3 py-2 text-center">{fmt$(m.labourCostPerBedDay)}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={m.agencyPercentage > 10 ? 'text-red-600 font-medium' : 'text-emerald-600'}>{fmtPct(m.agencyPercentage)}</span>
                        </td>
                        <td className="px-3 py-2 text-center">{fmtPct(m.overtimePercentage)}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={m.careMinutesPerResidentPerDay >= 215 ? 'text-emerald-600 font-medium' : 'text-red-600 font-medium'}>
                            {m.careMinutesPerResidentPerDay?.toFixed(0)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">{fmtPct(m.turnoverRateAnnual)}</td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex items-center justify-center gap-0.5">
                            {Array.from({ length: fac.starRating?.predicted || 0 }).map((_, i) => (
                              <Star key={i} className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Turnover prediction */}
          {turnoverPrediction && (
            <Card className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <UserX className="w-5 h-5 text-red-500" /> Turnover Risk Prediction
              </h3>
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-red-700">{turnoverPrediction.summary?.highRisk || 0}</div>
                  <div className="text-xs text-gray-500">High Risk</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-amber-700">{turnoverPrediction.summary?.mediumRisk || 0}</div>
                  <div className="text-xs text-gray-500">Medium Risk</div>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-emerald-700">{turnoverPrediction.summary?.lowRisk || 0}</div>
                  <div className="text-xs text-gray-500">Low Risk</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-gray-700">{fmtPct((turnoverPrediction.summary?.avgTurnoverRisk || 0) * 100)}</div>
                  <div className="text-xs text-gray-500">Avg Risk Score</div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Staff</th>
                      <th className="px-3 py-2 text-left">Role</th>
                      <th className="px-3 py-2 text-center">Tenure</th>
                      <th className="px-3 py-2 text-center"><Tooltip content="AI-predicted probability of turnover within 6 months based on tenure, shift patterns, and engagement signals"><span className="cursor-help">Risk Score</span></Tooltip></th>
                      <th className="px-3 py-2 text-center"><Tooltip content="High ≥70%, Medium 40-69%, Low <40%"><span className="cursor-help">Level</span></Tooltip></th>
                      <th className="px-3 py-2 text-left">Key Signals</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(turnoverPrediction.predictions || []).slice(0, 10).map(p => (
                      <tr key={p.staffId} className={`border-t border-gray-100 ${p.riskLevel === 'high' ? 'bg-red-50' : ''}`}>
                        <td className="px-3 py-2 font-medium">{p.staffName}</td>
                        <td className="px-3 py-2">{p.roleCode}</td>
                        <td className="px-3 py-2 text-center">{p.tenureYears?.toFixed(1)}y</td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-16 bg-gray-200 rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full ${p.riskLevel === 'high' ? 'bg-red-500' : p.riskLevel === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'}`}
                                style={{ width: `${p.turnoverRisk * 100}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium">{(p.turnoverRisk * 100).toFixed(0)}%</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase ${
                            p.riskLevel === 'high' ? 'bg-red-100 text-red-700' :
                            p.riskLevel === 'medium' ? 'bg-amber-100 text-amber-700' :
                            'bg-emerald-100 text-emerald-700'
                          }`}>{p.riskLevel}</span>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500">{(p.factors || []).join(', ') || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* Cost modelling */}
          <Card className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-indigo-600" /> Cost Modelling
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Occupancy: {scenarioOccupancy} beds
                  </label>
                  <input type="range" min={40} max={90} value={scenarioOccupancy} onChange={e => setScenarioOccupancy(Number(e.target.value))} className="w-full accent-indigo-600" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Agency %: {scenarioAgency}%
                  </label>
                  <input type="range" min={0} max={30} value={scenarioAgency} onChange={e => setScenarioAgency(Number(e.target.value))} className="w-full accent-indigo-600" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Overtime %: {scenarioOvertime}%
                  </label>
                  <input type="range" min={0} max={20} value={scenarioOvertime} onChange={e => setScenarioOvertime(Number(e.target.value))} className="w-full accent-indigo-600" />
                </div>
                <button onClick={handleRunScenario} className="w-full px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium text-sm">
                  Run Scenario
                </button>
              </div>
              <div className="md:col-span-2">
                {scenarioCost ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-indigo-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-indigo-700">{fmt$(scenarioCost.estimatedDailyCost)}</div>
                      <div className="text-xs text-gray-500">Daily Cost</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-purple-700">{fmt$(scenarioCost.estimatedWeeklyCost)}</div>
                      <div className="text-xs text-gray-500">Weekly Cost</div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-blue-700">{fmt$(scenarioCost.estimatedAnnualCost)}</div>
                      <div className="text-xs text-gray-500">Annual Cost</div>
                    </div>
                    <div className={`rounded-lg p-4 text-center ${scenarioCost.costDifference > 0 ? 'bg-red-50' : 'bg-emerald-50'}`}>
                      <div className={`text-2xl font-bold ${scenarioCost.costDifference > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                        {scenarioCost.costDifference > 0 ? '+' : ''}{fmt$(scenarioCost.costDifference)}/wk
                      </div>
                      <div className="text-xs text-gray-500">vs Current</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <div className="text-xl font-bold text-gray-700">{scenarioCost.careMinutesPerResident?.toFixed(0)}</div>
                      <div className="text-xs text-gray-500">Care min/res</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                      <div className="text-xl font-bold text-gray-700">{scenarioCost.demand?.totalRequired}</div>
                      <div className="text-xs text-gray-500">Staff Required</div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                    Adjust sliders and click "Run Scenario" to see projections
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Trend sparklines */}
          <Card className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-600" /> 12-Week Trends
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { label: 'Labour Cost / Bed Day', base: 300, variance: 40, color: 'indigo' },
                { label: 'Agency %', base: 8, variance: 5, color: 'amber' },
                { label: 'Care Minutes / Res / Day', base: 210, variance: 20, color: 'emerald' },
              ].map(metric => {
                const points = Array.from({ length: 12 }).map(() => metric.base + (Math.random() - 0.5) * metric.variance);
                const max = Math.max(...points);
                const min = Math.min(...points);
                const range = max - min || 1;
                return (
                  <div key={metric.label}>
                    <div className="text-sm font-medium text-gray-700 mb-2">{metric.label}</div>
                    <div className="flex items-end gap-1 h-16">
                      {points.map((p, i) => (
                        <div
                          key={i}
                          className={`flex-1 bg-${metric.color}-400 rounded-t`}
                          style={{ height: `${Math.max(10, ((p - min) / range) * 100)}%` }}
                          title={`Week ${i + 1}: ${p.toFixed(1)}`}
                        />
                      ))}
                    </div>
                    <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                      <span>W1</span><span>W12</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          TAB 9 — On-Call
          ═══════════════════════════════════════════════════════════════ */}
      {activeSubTab === 'oncall' && (
        <div className="space-y-6">
          {/* On-call roster */}
          <Card className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Phone className="w-5 h-5 text-indigo-600" /> On-Call Roster (This Week)
            </h3>
            {onCallRoster.length === 0 ? (
              <EmptyState
                icon={Phone}
                title="No On-Call Entries"
                description="No on-call roster entries for this week. Assign on-call staff to see entries here."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Staff</th>
                      <th className="px-3 py-2 text-left">Period</th>
                      <th className="px-3 py-2 text-center">Type</th>
                      <th className="px-3 py-2 text-right">Allowance</th>
                      <th className="px-3 py-2 text-center">Call-outs</th>
                      <th className="px-3 py-2 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {onCallRoster.map(entry => (
                      <tr key={entry.id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium">{entry.staffName}</td>
                        <td className="px-3 py-2 text-gray-500">
                          {fmtTime(entry.startDateTime)} — {fmtTime(entry.endDateTime)}
                        </td>
                        <td className="px-3 py-2 text-center capitalize">{entry.type}</td>
                        <td className="px-3 py-2 text-right font-medium">{fmt$(entry.allowanceRate)}</td>
                        <td className="px-3 py-2 text-center">{entry.callOuts?.length || 0}</td>
                        <td className="px-3 py-2 text-center"><StatusBadge status={entry.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Call-out log */}
          <Card className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5 text-indigo-600" /> Recent Call-Outs
            </h3>
            {(() => {
              const allCallOuts = onCallRoster.flatMap(entry =>
                (entry.callOuts || []).map(co => ({ ...co, staffName: entry.staffName, facilityId: entry.facilityId }))
              );
              if (allCallOuts.length === 0) return <p className="text-gray-500 text-sm py-4 text-center">No call-outs recorded.</p>;
              return (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-3 py-2 text-left">Staff</th>
                        <th className="px-3 py-2 text-left">Called At</th>
                        <th className="px-3 py-2 text-left">Reason</th>
                        <th className="px-3 py-2 text-center">Hours</th>
                        <th className="px-3 py-2 text-center">Overtime</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allCallOuts.map(co => (
                        <tr key={co.id} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium">{co.staffName}</td>
                          <td className="px-3 py-2 text-gray-500">{fmtTime(co.calledAt)}</td>
                          <td className="px-3 py-2">{co.reason || '—'}</td>
                          <td className="px-3 py-2 text-center">{co.hoursWorked || 0}</td>
                          <td className="px-3 py-2 text-center">
                            {co.overtimeApplicable
                              ? <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" />
                              : <XCircle className="w-4 h-4 text-gray-400 mx-auto" />
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </Card>

          {/* Escalation chain */}
          <Card className="p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-indigo-600" /> Escalation Chain
            </h3>
            <div className="flex flex-col md:flex-row gap-2">
              {[
                { level: 1, role: 'Shift Coordinator', sla: '5 min', icon: UserCheck },
                { level: 2, role: 'Facility Manager', sla: '15 min', icon: Briefcase },
                { level: 3, role: 'Regional Manager', sla: '30 min', icon: Building2 },
                { level: 4, role: 'Operations Director', sla: '1 hour', icon: Star },
              ].map((step, idx) => (
                <React.Fragment key={step.level}>
                  <div className="flex-1 bg-gray-50 rounded-lg p-4 text-center">
                    <step.icon className="w-6 h-6 text-indigo-600 mx-auto mb-2" />
                    <div className="text-sm font-bold text-gray-900">Level {step.level}</div>
                    <div className="text-xs text-gray-600">{step.role}</div>
                    <div className="text-[10px] text-gray-400 mt-1">SLA: {step.sla}</div>
                  </div>
                  {idx < 3 && (
                    <div className="flex items-center justify-center">
                      <ArrowRight className="w-5 h-5 text-gray-300 rotate-90 md:rotate-0" />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </Card>

          {/* Active escalations */}
          {escalationHistory && (
            <Card className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" /> Escalation History
              </h3>
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-gray-700">{escalationHistory.summary?.total || 0}</div>
                  <div className="text-xs text-gray-500">Total</div>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-emerald-700">{escalationHistory.summary?.resolved || 0}</div>
                  <div className="text-xs text-gray-500">Resolved</div>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-red-700">{escalationHistory.summary?.active || 0}</div>
                  <div className="text-xs text-gray-500">Active</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-blue-700">{escalationHistory.summary?.avgResponseMinutes || 0} min</div>
                  <div className="text-xs text-gray-500">Avg Response</div>
                </div>
              </div>
              {(escalationHistory.escalations || []).length > 0 ? (
                <div className="space-y-2">
                  {escalationHistory.escalations.map(esc => (
                    <div key={esc.id} className={`p-4 rounded-lg border ${esc.resolvedAt ? 'bg-gray-50 border-gray-200' : 'bg-red-50 border-red-200'}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-bold text-gray-900">{esc.reason}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Triggered: {fmtDate(esc.triggeredAt)} at {fmtTime(esc.triggeredAt)} — Level {esc.currentLevel}
                          </p>
                          {esc.responseTime && (
                            <p className="text-xs text-gray-500">Response: {esc.responseTime} min — Outcome: {esc.outcome || 'Pending'}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {esc.resolvedAt ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                              <CheckCircle className="w-3.5 h-3.5" /> Resolved
                            </span>
                          ) : (
                            <button
                              onClick={() => handleRespondEscalation(esc.id)}
                              className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700"
                            >
                              Respond
                            </button>
                          )}
                        </div>
                      </div>
                      {esc.escalationChain && (
                        <div className="flex gap-2 mt-3">
                          {esc.escalationChain.map(step => (
                            <div key={step.level} className={`text-[10px] px-2 py-1 rounded ${
                              step.respondedAt ? 'bg-emerald-100 text-emerald-700' :
                              step.notifiedAt ? 'bg-amber-100 text-amber-700' :
                              'bg-gray-100 text-gray-500'
                            }`}>
                              L{step.level}: {step.role || `Level ${step.level}`}
                              {step.respondedAt && ' (responded)'}
                              {step.notifiedAt && !step.respondedAt && ' (notified)'}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">No escalation events recorded.</p>
              )}
            </Card>
          )}
        </div>
      )}

      {/* ═══════════════════ Modals ═══════════════════ */}

      {/* Leave Request Modal */}
      {showLeaveModal && (
        <LeaveRequestModal
          onClose={() => setShowLeaveModal(false)}
          onSubmit={async (data) => {
            const res = await EnterpriseRosterService.submitLeaveRequest(data);
            if (res.success) {
              setShowLeaveModal(false);
              loadData();
            } else {
              showToast(res.error, 'error');
            }
          }}
        />
      )}

      {/* Blackout Period Modal */}
      {showBlackoutModal && (
        <BlackoutModal
          facilityId={facilityFilter}
          onClose={() => setShowBlackoutModal(false)}
          onSubmit={async (data) => {
            await EnterpriseRosterService.setBlackoutPeriod(data.facilityId, data.startDate, data.endDate, data.reason);
            setShowBlackoutModal(false);
            loadData();
          }}
        />
      )}

      {/* Marketplace Shift Detail Modal */}
      {selectedMarketplaceShift && (
        <MarketplaceDetailModal
          shift={selectedMarketplaceShift}
          onClose={() => setSelectedMarketplaceShift(null)}
          onSelect={handleSelectApplicant}
        />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════
   Sub-components: Modals
   ═══════════════════════════════════════════════════════════════════════ */

const LeaveRequestModal = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({
    staffId: 'staff-emma-thompson',
    staffName: 'Emma Thompson',
    facilityId: 'sunshine',
    leaveType: 'annual',
    startDate: '',
    endDate: '',
    partialDay: false,
    notes: '',
    medicalCertificateAttached: false,
  });

  const staffOptions = [
    { id: 'staff-james-chen', name: 'James Chen' },
    { id: 'staff-emma-thompson', name: 'Emma Thompson' },
    { id: 'staff-david-rodriguez', name: 'David Rodriguez' },
    { id: 'staff-michael-brown', name: 'Michael Brown' },
    { id: 'staff-sophie-wilson', name: 'Sophie Wilson' },
    { id: 'staff-grace-lee', name: 'Grace Lee' },
    { id: 'staff-daniel-wang', name: 'Daniel Wang' },
    { id: 'staff-hannah-smith', name: 'Hannah Smith' },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    const selected = staffOptions.find(s => s.id === form.staffId);
    onSubmit({ ...form, staffName: selected?.name || form.staffId });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-5 rounded-t-2xl flex justify-between items-center">
          <h2 className="text-xl font-bold">New Leave Request</h2>
          <button onClick={onClose} className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg"><XCircle className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Staff Member</label>
            <select value={form.staffId} onChange={e => setForm({ ...form, staffId: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500">
              {staffOptions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type</label>
            <select value={form.leaveType} onChange={e => setForm({ ...form, leaveType: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500">
              {['annual', 'personal', 'long_service', 'compassionate', 'study', 'toil', 'unpaid'].map(t => (
                <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input type="date" required value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input type="date" required value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.partialDay} onChange={e => setForm({ ...form, partialDay: e.target.checked })} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
              Partial day
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.medicalCertificateAttached} onChange={e => setForm({ ...form, medicalCertificateAttached: e.target.checked })} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
              Medical certificate
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" placeholder="Additional information..." />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">
              Cancel
            </button>
            <button type="submit" className="flex-1 px-4 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:from-indigo-600 hover:to-purple-700 font-medium">
              Submit Request
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BlackoutModal = ({ facilityId, onClose, onSubmit }) => {
  const [form, setForm] = useState({ facilityId, startDate: '', endDate: '', reason: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
        <div className="p-5 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Add Blackout Period</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><XCircle className="w-5 h-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input type="date" required value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input type="date" required value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
            <input type="text" required value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" placeholder="e.g. Christmas peak period" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">Cancel</button>
            <button type="submit" className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium">Add Blackout</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const MarketplaceDetailModal = ({ shift, onClose, onSelect }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-5 rounded-t-2xl flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold">Shift Applicants</h2>
            <p className="text-indigo-200 text-sm">{shift.facilityName || shift.facilityId} — {(shift.currentTier || '').replace(/_/g, ' ')}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg"><XCircle className="w-5 h-5" /></button>
        </div>
        <div className="p-6">
          {(shift.applicants || []).length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-6">No applicants yet for this shift.</p>
          ) : (
            <div className="space-y-3">
              {shift.applicants.map(app => {
                const reliabilityScore = (75 + Math.random() * 25).toFixed(0);
                const distanceKm = (2 + Math.random() * 20).toFixed(1);
                return (
                  <div key={app.staffId} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-indigo-300 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center text-sm font-bold text-indigo-700">
                        {app.staffName?.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{app.staffName}</div>
                        <div className="text-xs text-gray-500">{app.roleCode} — Applied {fmtDate(app.appliedAt)}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <div className="text-sm font-bold text-emerald-600">{reliabilityScore}%</div>
                        <div className="text-[10px] text-gray-400">Reliability</div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm font-medium text-gray-600">{distanceKm}km</div>
                        <div className="text-[10px] text-gray-400">Distance</div>
                      </div>
                      <div className="text-center">
                        {app.eligible
                          ? <CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" />
                          : <XCircle className="w-5 h-5 text-red-500 mx-auto" />
                        }
                        <div className="text-[10px] text-gray-400">Credentials</div>
                      </div>
                      <button
                        onClick={() => onSelect(shift.id, app.staffId)}
                        className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700"
                      >
                        Select
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnterpriseRoster;
